import { useMemo } from 'react';

/**
 * Custom hook for managing style filtering based on water content analysis
 */
export function useWaterBasedStyles(allStyles, waterAnalysis, waterThreshold = 5.0) {
    // Categorize styles based on their coastalOnly and landOnly flags
    const styleCategories = useMemo(() => {
        if (!allStyles || allStyles.length === 0) {
            return {
                land: [],
                coastal: []
            };
        }

        const categories = {
            land: [],
            coastal: []
        };

        allStyles.forEach(style => {
            // Categorize styles based on their flags, not their names
            if (style.coastalOnly) {
                categories.coastal.push(style);
            } else if (style.landOnly) {
                categories.land.push(style);
            } else {
                // Styles without restrictions can be used for both land and coastal
                // Add to both categories
                categories.land.push(style);
                categories.coastal.push(style);
            }
        });

        return categories;
    }, [allStyles]);

    // Filter styles based on water analysis and style settings
    const availableStyles = useMemo(() => {
        if (!allStyles || allStyles.length === 0) return [];
        
        if (!waterAnalysis || !waterAnalysis.categories) {
            // If no water analysis, show all styles
            return allStyles;
        }

        const { waterPercentage } = waterAnalysis;
        const isHighWater = waterPercentage >= waterThreshold;
        
        return allStyles.filter(style => {
            // Debug logging for water content filtering
            if (localStorage.getItem('waterDetectionDebug') === 'true') {
                console.log(`Style "${style.name}": coastalOnly=${style.coastalOnly}, landOnly=${style.landOnly}, waterPercentage=${waterPercentage}, threshold=${waterThreshold}, isHighWater=${isHighWater}`);
            }
            
            // Check style-specific water restrictions
            if (style.coastalOnly && !isHighWater) {
                // This is a coastal-only style but the route doesn't have enough water
                if (localStorage.getItem('waterDetectionDebug') === 'true') {
                    console.log(`Excluding coastal-only style "${style.name}" (water too low: ${waterPercentage}% < ${waterThreshold}%)`);
                }
                return false;
            }
            
            if (style.landOnly && isHighWater) {
                // This is a land-only style but the route has too much water
                if (localStorage.getItem('waterDetectionDebug') === 'true') {
                    console.log(`Excluding land-only style "${style.name}" (water too high: ${waterPercentage}% >= ${waterThreshold}%)`);
                }
                return false;
            }
            
            // Style passed all filters - show it
            if (localStorage.getItem('waterDetectionDebug') === 'true') {
                console.log(`Including style "${style.name}"`);
            }
            return true;
        });
    }, [waterAnalysis, allStyles, waterThreshold]);

    // Get recommended styles based on water percentage and style categories
    const recommendedStyles = useMemo(() => {
        if (!waterAnalysis) {
            return [];
        }

        const { waterPercentage } = waterAnalysis;
        const isHighWater = waterPercentage >= waterThreshold;
        
        // Choose the appropriate category based on water content
        const recommendedCategory = isHighWater ? 'coastal' : 'land';
        const categoryStyles = styleCategories[recommendedCategory] || [];
        
        // Filter the category styles through availableStyles to respect coastal/land restrictions
        return categoryStyles.filter(style => 
            availableStyles.some(available => available.id === style.id)
        );
    }, [waterAnalysis, styleCategories, availableStyles, waterThreshold]);

    // Get styles that should be shown by default (all available styles)
    const defaultStyles = useMemo(() => {
        return availableStyles;
    }, [availableStyles]);

    // Get water content summary
    const waterSummary = useMemo(() => {
        if (!waterAnalysis) {
            return {
                percentage: 0,
                description: 'No water analysis available',
                categories: { land: true, coastal: false }
            };
        }

        const { waterPercentage } = waterAnalysis;
        let description = '';

        if (waterPercentage === 0) {
            description = 'No water detected - perfect for land-based styles';
        } else if (waterPercentage < 1) {
            description = 'Minimal water content - ideal for land styles';
        } else if (waterPercentage < waterThreshold) {
            description = `Low water content (${waterPercentage}% < ${waterThreshold}%) - suitable for land styles`;
        } else if (waterPercentage < 20) {
            description = `Moderate water content (${waterPercentage}% ≥ ${waterThreshold}%) - great for coastal styles`;
        } else {
            description = 'High water content - perfect for coastal styles';
        }

        return {
            percentage: waterPercentage,
            description,
            categories: { land: waterPercentage < waterThreshold, coastal: waterPercentage >= waterThreshold }
        };
    }, [waterAnalysis, waterThreshold]);

    return {
        availableStyles,
        recommendedStyles,
        defaultStyles,
        styleCategories,
        waterSummary,
        isWaterAnalyzed: !!waterAnalysis,
        waterAnalysis,
        waterThreshold
    };
} 